import {Component} from '@angular/core';
import {Platform} from '@ionic/angular';
import {Router} from '@angular/router';

import * as moment from 'moment';
import 'moment-timezone';

import {UserService} from './services/user.service';
import {NavigationService} from './services/navigation.service';
import {AlertsService} from './services/alerts.service';
import {LocalDataService} from './services/local-data.service';
import {PlatformService} from './services/platform.service';
import {Events} from './services/events.service';
import {VersionService} from './services/version.service';

import {UserObject} from './objects/user.object';

import {AppUtils} from './app.utils';
import {AppConfig} from './app.config';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent {

	pages: Array<{
		id: number, title: string, url: any, internal: boolean, params: any, selected: boolean, icon: string, iconActive: string, expanded?: boolean
		pages: Array<{
			id: number, title: string, url: any, params: any, selected: boolean
		}>
	}>;

	userConnected: UserObject;
	appVersion: string = null;

	constructor(private platform: Platform, private userService: UserService, public navigationService: NavigationService, private alertsService: AlertsService, private localDataService: LocalDataService,
				private platformService: PlatformService, private events: Events, private router: Router, private versionService: VersionService) {
		this.initializeApp();
	}

	initializeApp() {
		this.platform.ready().then(async () => {
			this.initPages();
			this.events.subscribe('pageSelected', (id) => {
				for (const curPage of this.pages) {
					if (id !== null && curPage.id === id) {
						curPage.selected = true;
					} else {
						let pageFound = false;
						if (curPage.pages) {
							for (const curSubPage of curPage.pages) {
								if (id !== null && curSubPage.id === id) {
									pageFound = true;
									curSubPage.selected = true;
									curPage.selected = true;
									curPage.expanded = true;
								} else {
									curSubPage.selected = false;
								}
							}
						}
						if (!pageFound) {
							curPage.selected = false;
							curPage.expanded = false;
						}
					}
				}
			});

			moment.locale('fr');
			moment.tz.setDefault('Europe/Paris');

			await this.localDataService.configure();
			this.appVersion = await this.versionService.getAppVersion();
			const htmlTag = document.getElementById('html-tag');
			if (!this.platformService.isDesktop) {
				if (this.platformService.isTablet) {
					htmlTag.classList.add('atmosphere-tablet');
				} else {
					htmlTag.classList.add('atmosphere-mobile');
				}
			} else {
				htmlTag.classList.add('atmosphere-desktop');
			}
			const versionOutdated = await this.versionService.isAppVersionOutdated();
			if (versionOutdated) {
				await this.alertsService.messageAlert('L\'application a été mise à jour', 'Merci de cliquer sur OK pour rafraîchir votre navigateur.');
				if (this.platformService.isDesktop) {
					location.reload();
				}
			}
			this.events.subscribe('userConnected', userConnected => {
				this.userConnected = userConnected;
				this.initPages();
			});
			const isUserConnected = await this.userService.isUserConnected();
			if (isUserConnected) {
				try {
					this.userConnected = await this.userService.getUserConnected();
					this.initPages();
					await AppUtils.delay(500);
					if (this.router.url === '/') {
						await this.navigationService.goToInternal('/home');
					}
				} catch (error) {
					await this.navigationService.goToInternal('/login');
				}
			} else {
				await this.navigationService.goToInternal('/login');
			}
			await AppUtils.delay(100);
			const url = this.router.url.split('?')[0];
			this.navigationService.pageSelected(url, this.navigationService.getParamsFromUrl(this.router.url));
		});
	}

	async deconnexion() {
		const confirmed = await this.alertsService.confirmationAlert('Déconnexion', 'Êtes-vous sûr(e) de vouloir vous déconnecter ?', 'Se déconnecter');
		if (confirmed) {
			await this.userService.deconnexion();
		}
	}

	initPages() {
		this.pages = [];

		this.pages.push({
			id: 0, title: 'Accueil', url: '/home', internal: true, params: null, selected: true, icon: '/assets/imgs/menu-accueil.svg', iconActive: '/assets/imgs/menu-accueil-active.svg',
			pages: null
		});
		const conventionPages = [
			{id: 2, title: 'Liste des conventions', url: '/conventions', params: {viewType: 'all'}, selected: false},
			{id: 3, title: 'Suivi d\'activité', url: '/suivi-conventions', params: {viewType: 'suiviActivite'}, selected: false},
			{id: 4, title: 'Temps passé par convention', url: '/suivi-conventions', params: {viewType: 'tempsPasse'}, selected: false}
		];

		if (this.userConnected?.isAuthorized('Convention', 'Ajouter')) {
			conventionPages.unshift({id: 1, title: 'Nouvelle convention', url: '/conventions/new', params: null, selected: false});
		}

		this.pages.push({
			id: null, title: 'Conventions', url: null, internal: true, params: null, selected: false, icon: '/assets/imgs/menu-conv.svg', iconActive: '/assets/imgs/menu-conv-active.svg', expanded: false,
			pages: conventionPages
		});

		this.pages.push({
			id: null, title: 'Plannings', url: null, internal: true, params: null, selected: false, icon: '/assets/imgs/menu-planning.svg', iconActive: '/assets/imgs/menu-planning-active.svg', expanded: false,
			pages: [
				{id: 5, title: 'Planning projets', url: '/planning', params: {viewType: 'resourceView'}, selected: false},
				// {id: 6, title: 'Planning de charge', url: '/planning', params: {viewType: 'plan-de-charge'}, selected: false}
			]
		});
		const feuilleHeurPages = [
			{id: 14, title: 'Liste des feuilles d\'heures', url: '/feuilles-heures', params: {viewType: 'liste'}, selected: false},
			{id: 15, title: 'Temps passé par agent', url: '/feuilles-heures', params: {viewType: 'analyse'}, selected: false},
		];

		if (this.userConnected?.isAuthorized('FeuilleHeures', 'Ajouter')) {
			feuilleHeurPages.unshift({id: 13, title: 'Nouvelle feuille d\'heures', url: '/feuilles-heures/new', params: {viewType: 'liste'}, selected: false});
		}

		this.pages.push({
			id: null, title: 'Suivi des heures', url: null, internal: true, params: null, selected: false, icon: '/assets/imgs/menu-heures.svg', iconActive: '/assets/imgs/menu-heures-active.svg', expanded: false,
			pages: feuilleHeurPages
		});
		const marchePages = [
			{id: 12, title: 'Liste des marchés', url: '/marches', params: {viewType: 'all'}, selected: false}
		];

		if (this.userConnected?.isAuthorized('Marches', 'Ajouter')) {
			marchePages.unshift({id: 11, title: 'Nouveau marché', url: '/marches/new', params: null, selected: false});
		}

		this.pages.push({
			id: null, title: 'Marchés', url: null, internal: true, params: null, selected: false, icon: '/assets/imgs/menu-marches.svg', iconActive: '/assets/imgs/menu-marches-active.svg', expanded: false,
			pages: marchePages
		});
		if (this.userConnected?.isAuthorized('Indicateurs', 'Visualiser')) {
			const pages = [];
			if (this.userConnected?.isAuthorized('Indicateurs', 'VisualiserSuiviAdherents')) {
				pages.push({id: 7, title: 'Suivi des adhérents', url: '/indicateurs', params: {viewType: 'suiviAdherents'}, selected: false});
			}
			if (this.userConnected?.isAuthorized('Indicateurs', 'VisualiserSuiviConventions')) {
				pages.push({id: 8, title: 'Suivi des conventions', url: '/indicateurs', params: {viewType: 'suiviConventions'}, selected: false});
			}
			if (this.userConnected?.isAuthorized('Indicateurs', 'VisualiserSuiviChiffreAffaires')) {
				pages.push({id: 9, title: 'Suivi du chiffre d\'affaires', url: '/indicateurs', params: {viewType: 'suiviChiffreAffaires'}, selected: false});
			}
			if (this.userConnected?.isAuthorized('Indicateurs', 'VisualiserSuiviFacturation')) {
				pages.push({id: 10, title: 'Suivi de la facturation', url: '/indicateurs', params: {viewType: 'suiviFacturation'}, selected: false});
			}
			if (this.userConnected?.isAuthorized('Indicateurs', 'VisualiserSuiviReste')) {
				pages.push({id: 16, title: 'Suivi du reste à réaliser', url: '/indicateurs', params: {viewType: 'suiviReste'}, selected: false});
			}
			this.pages.push({
				id: null, title: 'Indicateurs', url: null, internal: true, params: null, selected: false, icon: '/assets/imgs/menu-indic.svg', iconActive: '/assets/imgs/menu-indic-active.svg', expanded: false,
				pages: pages
			});
		}
		this.pages.push({
			id: null, title: 'Adhérents', url: AppConfig.UrlAdherents, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-adherents.svg', iconActive: '/assets/imgs/menu-adherents-active.svg',
			pages: null
		});
		this.pages.push({
			id: null, title: 'Cotisations', url: AppConfig.UrlCotisations, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-cotisations.svg', iconActive: '/assets/imgs/menu-cotisations-active.svg',
			pages: null
		});
		this.pages.push({
			id: null, title: 'Factures', url: AppConfig.UrlFactures, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-fact.svg', iconActive: '/assets/imgs/menu-fact-active.svg',
			pages: null
		});
		this.pages.push({
			id: null, title: 'Utilisateurs', url: AppConfig.UrlUtilisateurs, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-users.svg', iconActive: '/assets/imgs/menu-users-active.svg',
			pages: null
		});
		this.pages.push({
			id: null, title: 'Articles', url: AppConfig.UrlArticles, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-articles.svg', iconActive: '/assets/imgs/menu-articles-active.svg',
			pages: null
		});
		this.pages.push({
			id: null, title: 'Paramêtres', url: AppConfig.UrlParametres, internal: false, params: null, selected: false, icon: '/assets/imgs/menu-settings.svg', iconActive: '/assets/imgs/menu-settings-active.svg',
			pages: null
		});
	}

	expandCollapsePage(page) {
		page.expanded = !!!page.expanded;
	}
}
